import React, { useState, useEffect } from 'react';
import { CountriesAPI } from '../api';

export default function AddressInput({ 
  value = '', 
  onChange, 
  label = 'Complete Address',
  required = false,
  className = '',
  placeholder = 'Enter address details'
}) {
  const [countries, setCountries] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [street, setStreet] = useState('');
  const [city, setCity] = useState('');
  const [stateProv, setStateProv] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [loading, setLoading] = useState(true);

  // Load countries on component mount
  useEffect(() => {
    const loadCountries = async () => {
      try {
        const token = localStorage.getItem('token');
        const countriesList = await CountriesAPI.list(token);
        const activeCountries = countriesList.filter(country => country.active);
        setCountries(activeCountries);
        
        // Try to parse existing value to extract country and address
        if (value) {
          parseExistingValue(value, activeCountries);
        }
      } catch (error) {
        console.error('Failed to load countries:', error);
      } finally {
        setLoading(false);
      }
    };

    loadCountries();
  }, []);

  // Parse existing address value to extract country and address details
  const parseExistingValue = (fullValue, countriesList) => {
    // Reset
    const resetParts = () => { setStreet(''); setCity(''); setStateProv(''); setPostalCode(''); };
    if (!fullValue) { setSelectedCountry(null); resetParts(); return; }

    for (const country of countriesList) {
      if (fullValue.endsWith(`, ${country.name}`)) {
        setSelectedCountry(country);
        const before = fullValue.substring(0, fullValue.length - `, ${country.name}`.length);
        const parts = before.split(',').map(s => s.trim()).filter(Boolean);
        setStreet(parts[0] || '');
        setCity(parts[1] || '');
        setStateProv(parts[2] || '');
        setPostalCode(parts[3] || '');
        return;
      }
    }
    setSelectedCountry(null);
    const parts = fullValue.split(',').map(s => s.trim()).filter(Boolean);
    setStreet(parts[0] || '');
    setCity(parts[1] || '');
    setStateProv(parts[2] || '');
    setPostalCode(parts[3] || '');
  };

  // Handle country selection
  const handleCountryChange = (countryId) => {
    const country = countries.find(c => c._id === countryId);
    setSelectedCountry(country);
    
    const composed = [street, city, stateProv, postalCode].filter(Boolean).join(', ');
    const fullValue = country ? `${composed}, ${country.name}`.replace(/^,\s*/, '') : composed;
    onChange(fullValue);
  };

  const updateComposed = (next = {}) => {
    const s = next.street !== undefined ? next.street : street;
    const c = next.city !== undefined ? next.city : city;
    const sp = next.stateProv !== undefined ? next.stateProv : stateProv;
    const pc = next.postalCode !== undefined ? next.postalCode : postalCode;
    const composed = [s, c, sp, pc].filter(Boolean).join(', ');
    const fullValue = selectedCountry ? `${composed}, ${selectedCountry.name}`.replace(/^,\s*/, '') : composed;
    onChange(fullValue);
  };

  // Handle external value changes
  useEffect(() => {
    const composed = [street, city, stateProv, postalCode].filter(Boolean).join(', ');
    const expectedValue = selectedCountry ? `${composed}, ${selectedCountry.name}`.replace(/^,\s*/, '') : composed;
    if (value !== expectedValue) {
      parseExistingValue(value, countries);
    }
  }, [value, countries, selectedCountry, street, city, stateProv, postalCode]);

  if (loading) {
    return (
      <div className={className}>
        <label className="text-xs text-slate-600">
          {label} {required && '*'}
        </label>
        <div className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 bg-slate-50">
          Loading countries...
        </div>
      </div>
    );
  }

  return (
    <div className={className}>
      <label className="text-xs text-slate-600">
        {label} {required && '*'}
      </label>
      
      <div className="mt-1 space-y-3">
        {/* Country Selector */}
        <div>
          <label className="text-xs text-slate-500 mb-1 block">Country *</label>
          <select
            value={selectedCountry?._id || ''}
            onChange={(e) => handleCountryChange(e.target.value)}
            className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500 hover:border-slate-300 transition-colors"
            required={required}
          >
            <option value="">Select Country</option>
            {countries.map(country => (
              <option key={country._id} value={country._id}>
                {country.name} ({country.code})
              </option>
            ))}
          </select>
        </div>

        {/* Structured address fields */}
        <div className={`grid grid-cols-1 md:grid-cols-2 gap-2 ${selectedCountry ? '' : 'opacity-60 pointer-events-none'}`}>
          <input
            type="text"
            value={street}
            onChange={(e) => { setStreet(e.target.value); updateComposed({ street: e.target.value }); }}
            placeholder="Street address"
            className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500 hover:border-slate-300"
            required={required}
            disabled={!selectedCountry}
          />
          <input
            type="text"
            value={city}
            onChange={(e) => { setCity(e.target.value); updateComposed({ city: e.target.value }); }}
            placeholder="City"
            className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500 hover:border-slate-300"
            required={required}
            disabled={!selectedCountry}
          />
          <input
            type="text"
            value={stateProv}
            onChange={(e) => { setStateProv(e.target.value); updateComposed({ stateProv: e.target.value }); }}
            placeholder="State / Province"
            className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500 hover:border-slate-300"
            required={required}
            disabled={!selectedCountry}
          />
          <input
            type="text"
            value={postalCode}
            onChange={(e) => { setPostalCode(e.target.value); updateComposed({ postalCode: e.target.value }); }}
            placeholder="Postal code"
            className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500 hover:border-slate-300"
            required={required}
            disabled={!selectedCountry}
          />
        </div>
      </div>
      
      {/* Helper text */}
      <div className="mt-2">
        {selectedCountry ? (
          <div className="text-xs text-slate-600 bg-slate-50 px-2 py-1 rounded-lg border border-slate-200">
            <span className="font-medium">{selectedCountry.name}</span> selected. Please include street address, city, state/province, postal code.
          </div>
        ) : (
          <div className="text-xs text-slate-500 bg-amber-50 px-2 py-1 rounded-lg border border-amber-200">
            Please select a country first
          </div>
        )}
      </div>
    </div>
  );
}
